/**
 * 구독자 수로 켜고 끄는 폴링 스냅샷 컨트롤러.
 *
 * 화면 여러 곳이 같은 전역 값을 보여줄 때(사이드바 상주 위젯 등) 폴링 주체를 모듈 싱글턴 하나로
 * 모으는 공통 골격이다. 소비처는 `load` 와 주기만 전달한다.
 *
 * 소유하는 정책:
 * - 구독 수가 0→1 이 될 때만 타이머를 시작하고 1→0 이 되면 멈춘다. 소비처가 둘이어도 호출은 한 번이다.
 * - 마지막 스냅샷은 구독이 끊겨도 남는다. 그래서 페이지를 이동해도 스켈레톤부터 다시 시작하지 않는다.
 * - 백그라운드 탭에서는 타이머를 멈춘다. 탭이 돌아오면 즉시 한 번 조회해 오래된 값을 남기지 않는다.
 * - 직전 응답이 오기 전에 같은 조회를 다시 보내지 않는다(`docs/rules.md` §7).
 * - 조회가 실패해도 직전 값은 유지하고 `failed` 만 설정한다. stale 표시는 소비처가 정한다.
 */
export type PollingSnapshot<Data> = {
  // 마지막으로 성공한 값. 첫 적재 전에는 null 이다. 그래서 "값이 0" 과 구분된다.
  data: Data | null;
  // 첫 적재 전에만 true. 다시 조회하는 중에는 직전 값을 그대로 보여준다.
  loading: boolean;
  // 마지막 조회가 실패했는지. data 가 함께 있으면 그 값은 직전 성공분(stale)이다.
  failed: boolean;
};

export type PollingControllerOptions<Data, RefreshArgs> = {
  load: (args?: RefreshArgs) => Promise<Data>;
  intervalMs: number;
  // 실패 기록 방법은 도메인이 정한다. 골격은 로거를 고르지 않는다.
  onLoadError: (error: unknown) => void;
};

export class PollingController<Data, RefreshArgs = void> {
  private readonly options: PollingControllerOptions<Data, RefreshArgs>;
  private snapshot: PollingSnapshot<Data>;
  private readonly listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  // 폴링과 수동 새로고침이 겹칠 때 같은 조회를 두 번 보내지 않게 하는 잠금 플래그.
  private inFlight = false;

  constructor(options: PollingControllerOptions<Data, RefreshArgs>) {
    this.options = options;
    this.snapshot = this.initialSnapshot();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    if (this.listeners.size === 1) {
      this.start();
    }
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) {
        this.stop();
      }
    };
  };

  getSnapshot = (): PollingSnapshot<Data> => this.snapshot;

  refresh = async (args?: RefreshArgs): Promise<void> => {
    if (this.inFlight) {
      return;
    }
    this.inFlight = true;
    try {
      const data = await this.options.load(args);
      this.publish({ data, loading: false, failed: false });
    } catch (error: unknown) {
      this.options.onLoadError(error);
      this.publish({ data: this.snapshot.data, loading: false, failed: true });
    } finally {
      this.inFlight = false;
    }
  };

  // 테스트 전용. 모듈 싱글턴이므로 케이스 사이에 상태가 남지 않게 초기화한다.
  resetForTest(): void {
    this.stop();
    this.listeners.clear();
    this.inFlight = false;
    this.snapshot = this.initialSnapshot();
  }

  private initialSnapshot(): PollingSnapshot<Data> {
    return { data: null, loading: true, failed: false };
  }

  private start(): void {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
    void this.refresh();
    this.startTimer();
  }

  private stop(): void {
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    }
    this.clearTimer();
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      this.clearTimer();
      return;
    }
    void this.refresh();
    this.startTimer();
  };

  private startTimer(): void {
    this.clearTimer();
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return;
    }
    this.timer = setInterval(() => void this.refresh(), this.options.intervalMs);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  // 값이 같으면 알리지 않는다. useSyncExternalStore 는 참조로 비교한다. 매번 새 객체를 보내면
  // 숫자가 바뀌지 않은 주기마다 구독자가 불필요하게 다시 그린다.
  private publish(next: PollingSnapshot<Data>): void {
    const previous = this.snapshot;
    if (
      Object.is(previous.data, next.data) &&
      previous.loading === next.loading &&
      previous.failed === next.failed
    ) {
      return;
    }
    this.snapshot = next;
    this.listeners.forEach((listener) => listener());
  }
}
